import {
  POLICY_ADMIN_PATH,
  POLICY_ENTERED_LABEL,
  POLICY_SKIP_AI,
  POLICY_SKIP_EXCLUDED,
  POLICY_SKIP_GENERIC,
  POLICY_SKIP_RESOLVED,
  POLICY_SOURCE_DEFAULT,
  POLICY_SOURCE_OVERRIDE,
  POLICY_SOURCE_OVERRIDE_FALLBACK,
  POLICY_STEP_LABELS,
} from '../constants';
import { formatBannerDateTime } from '../StateBanner/utils';
import type { IdvOpsResponse, IdvPolicy, IdvPolicySource, IdvPolicyStep, IdvPolicyStepKey } from '../types';
import type { PolicyBadgeTone, PolicyStepView, PolicyStripView } from './types';

const STEP_KEYS: IdvPolicyStepKey[] = ['reminder_1', 'reminder_2', 'auto_withdraw'];

const DAY_FIELD: Record<IdvPolicyStepKey, 'reminder_1_days' | 'reminder_2_days' | 'auto_withdraw_days'> = {
  reminder_1: 'reminder_1_days',
  reminder_2: 'reminder_2_days',
  auto_withdraw: 'auto_withdraw_days',
};

export const getPartnerIdvPolicyAdminUrl = (): string => {
  const configured = String(import.meta.env.VITE_PARTNER_IDV_POLICY_ADMIN_PATH || '').trim();
  const path = configured || POLICY_ADMIN_PATH;
  const apiBase = String(import.meta.env.VITE_API_URL || '').replace(/\/v1\/?$/, '');
  return `${apiBase}${path.startsWith('/') ? path : `/${path}`}`;
};

export const getSourceTagLabel = (source: IdvPolicySource, partnerName?: string): string => {
  if (source !== 'partner_override') return POLICY_SOURCE_DEFAULT;
  const name = partnerName?.trim();
  return name ? POLICY_SOURCE_OVERRIDE(name) : POLICY_SOURCE_OVERRIDE_FALLBACK;
};

export const getPolicyBadgeTone = (badge: string): PolicyBadgeTone => {
  const lower = badge.toLowerCase();
  if (lower.includes('excluded')) return 'info';
  if (lower.includes('not applicable') || lower.includes('withdrawn')) return 'neutral';
  const match = badge.match(/(\d+)\s+days?\s+remaining/i);
  if (match) return Number(match[1]) <= 3 ? 'danger' : 'warning';
  return 'neutral';
};

const dayFor = (policy: IdvPolicy, key: IdvPolicyStepKey): number | undefined => {
  const effective = policy.effective?.find((item) => item.key === key);
  if (effective?.day != null) return effective.day;
  const step = policy.steps.find((item) => item.key === key);
  if (step?.day != null) return step.day;
  const override = policy.overrides[DAY_FIELD[key]];
  if (override != null) return override;
  return policy.system_defaults[DAY_FIELD[key]];
};

const sourceFor = (policy: IdvPolicy, key: IdvPolicyStepKey): IdvPolicySource => {
  const step = policy.steps.find((item) => item.key === key);
  if (step?.source) return step.source;
  const effective = policy.effective?.find((item) => item.key === key);
  return effective?.source ?? 'system_default';
};

const skipMeta = (ops: IdvOpsResponse): string => {
  if (ops.resolution.resolved_kind === 'ai') return POLICY_SKIP_AI;
  if (ops.resolution.resolved_attempt_id != null) return POLICY_SKIP_RESOLVED;
  if (ops.exclude.excluded) return POLICY_SKIP_EXCLUDED;
  return POLICY_SKIP_GENERIC;
};

const reminderMeta = (ops: IdvOpsResponse, step: IdvPolicyStep | undefined): string | null => {
  if (step?.meta?.trim()) return step.meta.trim();
  if (step?.state === 'skipped') return skipMeta(ops);
  return null;
};

const reminderStep = (ops: IdvOpsResponse, key: IdvPolicyStepKey, partnerName?: string): PolicyStepView => {
  const step = ops.policy.steps.find((item) => item.key === key);
  const day = dayFor(ops.policy, key);
  const source = sourceFor(ops.policy, key);
  const label = day != null ? `${POLICY_STEP_LABELS[key]} · Day ${day}` : POLICY_STEP_LABELS[key];

  return {
    key,
    label,
    meta: reminderMeta(ops, step),
    sourceLabel: getSourceTagLabel(source, partnerName),
    sourceKind: source === 'partner_override' ? 'override' : 'default',
    state: step?.state ?? 'upcoming',
  };
};

export const getPolicyStrip = (ops: IdvOpsResponse, partnerName?: string): PolicyStripView => {
  const entered = ops.policy.entered_id_review_at;

  return {
    copy: ops.policy.copy,
    badge: ops.policy.badge,
    badgeReason: ops.policy.reason?.trim() || null,
    badgeTone: getPolicyBadgeTone(ops.policy.badge),
    steps: [
      {
        key: 'entered',
        label: POLICY_ENTERED_LABEL,
        meta: entered ? formatBannerDateTime(entered) : null,
        sourceLabel: null,
        sourceKind: null,
        state: 'done',
      },
      ...STEP_KEYS.map((key) => reminderStep(ops, key, partnerName)),
    ],
  };
};
